import React, { useState, useEffect } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    getExpandedRowModel,
    getPaginationRowModel,
    flexRender,
    type ColumnDef,
    type SortingState,
    type ColumnFiltersState,
    type ExpandedState,
    type Row,
    type RowSelectionState,
} from '@tanstack/react-table';
import './DataTable.scss';

const DEFAULT_COLUMN_WIDTH = 180;
const MAX_CONFIGURED_COLUMN_WIDTH = 1000;

const getColumnWidth = (columnDef: {
    size?: number;
    maxSize?: number;
}): number => {
    if (
        columnDef.maxSize !== undefined &&
        columnDef.maxSize < MAX_CONFIGURED_COLUMN_WIDTH
    ) {
        return columnDef.maxSize;
    }

    if (
        columnDef.size !== undefined &&
        columnDef.size < MAX_CONFIGURED_COLUMN_WIDTH
    ) {
        return columnDef.size;
    }

    return DEFAULT_COLUMN_WIDTH;
};

export interface DataTableProps<TData> {
    data: TData[];
    columns: ColumnDef<TData, any>[];
    // Sorting
    sorting?: boolean;
    /** Sort on the server: skip the local sorted row model and report state via onSortingChange. */
    manualSorting?: boolean;
    onSortingChange?: (state: SortingState) => void;
    // Column filtering
    columnFiltering?: boolean;
    /** Filter on the server: skip the local filtered row model and report state via onColumnFiltersChange. */
    manualFiltering?: boolean;
    onColumnFiltersChange?: (state: ColumnFiltersState) => void;
    // Global filter (search)
    globalFilter?: string;
    // Row selection (checkable)
    rowSelection?: boolean;
    onRowSelectionChange?: (selectedRows: TData[]) => void;
    /** Controlled selection state for preserving selection through data refreshes. */
    rowSelectionState?: RowSelectionState;
    onRowSelectionStateChange?: (state: RowSelectionState) => void;
    /** Stable key per row. Without it selection is keyed by index and breaks when data changes. */
    getRowId?: (row: TData) => string;
    /** Changes when a paged result changes, clearing selections from the previous page. */
    resetSelectionKey?: string | number;
    // Expandable rows
    expandable?: boolean;
    getSubRows?: (row: TData) => TData[] | undefined;
    renderExpandedRow?: (row: Row<TData>) => React.ReactNode;
    // Pagination (external — component does NOT render pagination UI)
    pageIndex?: number;
    pageSize?: number;
    manualPagination?: boolean;
    totalRows?: number;
    // Row click
    onRowClick?: (row: TData) => void;
    // Styling
    striped?: boolean;
    condensed?: boolean;
    className?: string;
}

function DataTable<TData>({
    data,
    columns,
    sorting = false,
    manualSorting = false,
    onSortingChange,
    columnFiltering = false,
    manualFiltering = false,
    onColumnFiltersChange,
    globalFilter,
    rowSelection = false,
    onRowSelectionChange,
    rowSelectionState: controlledRowSelectionState,
    onRowSelectionStateChange,
    getRowId,
    resetSelectionKey,
    expandable = false,
    getSubRows,
    renderExpandedRow,
    pageIndex,
    pageSize,
    manualPagination = false,
    totalRows,
    onRowClick,
    striped = false,
    condensed = false,
    className,
}: DataTableProps<TData>) {
    const [sortingState, setSortingState] = useState<SortingState>([]);
    const [columnFiltersState, setColumnFiltersState] =
        useState<ColumnFiltersState>([]);
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [internalRowSelectionState, setInternalRowSelectionState] =
        useState<RowSelectionState>({});
    const rowSelectionState =
        controlledRowSelectionState ?? internalRowSelectionState;
    const isRowSelectionControlled = controlledRowSelectionState !== undefined;
    const [filtersVisible, setFiltersVisible] = useState<
        Record<string, boolean>
    >({});

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting: sorting ? sortingState : undefined,
            columnFilters: columnFiltering ? columnFiltersState : undefined,
            globalFilter: globalFilter ?? undefined,
            expanded: expandable ? expanded : undefined,
            rowSelection: rowSelection ? rowSelectionState : undefined,
            ...(pageIndex !== undefined &&
            pageSize !== undefined &&
            !manualPagination
                ? { pagination: { pageIndex, pageSize } }
                : {}),
        },
        onSortingChange: sorting
            ? (updater) => {
                  const next =
                      typeof updater === 'function'
                          ? updater(sortingState)
                          : updater;
                  setSortingState(next);
                  onSortingChange?.(next);
              }
            : undefined,
        onColumnFiltersChange: columnFiltering
            ? (updater) => {
                  const next =
                      typeof updater === 'function'
                          ? updater(columnFiltersState)
                          : updater;
                  setColumnFiltersState(next);
                  onColumnFiltersChange?.(next);
              }
            : undefined,
        onExpandedChange: expandable ? setExpanded : undefined,
        onRowSelectionChange: rowSelection
            ? (updater) => {
                  const next =
                      typeof updater === 'function'
                          ? updater(rowSelectionState)
                          : updater;

                  if (isRowSelectionControlled) {
                      onRowSelectionStateChange?.(next);
                  } else {
                      setInternalRowSelectionState(next);
                  }
              }
            : undefined,
        ...(getRowId ? { getRowId: (row: TData) => getRowId(row) } : {}),
        getCoreRowModel: getCoreRowModel(),
        ...(sorting && !manualSorting
            ? { getSortedRowModel: getSortedRowModel() }
            : {}),
        ...(manualSorting ? { manualSorting: true } : {}),
        ...((columnFiltering && !manualFiltering) || globalFilter
            ? { getFilteredRowModel: getFilteredRowModel() }
            : {}),
        ...(manualFiltering ? { manualFiltering: true } : {}),
        ...(expandable
            ? { getExpandedRowModel: getExpandedRowModel(), getSubRows }
            : {}),
        ...(!manualPagination && pageSize !== undefined
            ? { getPaginationRowModel: getPaginationRowModel() }
            : {}),
        ...(manualPagination && totalRows !== undefined
            ? {
                  manualPagination: true,
                  pageCount: Math.ceil(totalRows / (pageSize ?? 10)),
              }
            : {}),
        enableRowSelection: rowSelection,
    });

    // Drop selections whose rows are no longer present, so the callback matches what is ticked.
    useEffect(() => {
        if (!rowSelection || isRowSelectionControlled) return;

        const visibleIds = new Set(table.getRowModel().rows.map((r) => r.id));
        const stale = Object.keys(rowSelectionState).filter(
            (id) => rowSelectionState[id] && !visibleIds.has(id)
        );

        if (stale.length > 0) {
            setInternalRowSelectionState((prev) => {
                const next = { ...prev };
                stale.forEach((id) => delete next[id]);
                return next;
            });
        }
    }, [data, isRowSelectionControlled, rowSelection]);

    useEffect(() => {
        if (rowSelection) {
            setInternalRowSelectionState({});
        }
    }, [resetSelectionKey, rowSelection, isRowSelectionControlled]);

    useEffect(() => {
        if (!rowSelection || !onRowSelectionChange) return;

        onRowSelectionChange(
            table.getSelectedRowModel().rows.map((r) => r.original)
        );
    }, [rowSelectionState, data, rowSelection]);

    const tableClasses = [
        'data-table',
        data.length === 0 ? 'data-table--empty' : '',
        striped ? 'data-table--striped' : '',
        condensed ? 'data-table--condensed' : '',
        className ?? '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={tableClasses}>
            <table style={{ width: '100%', tableLayout: 'fixed' }}>
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {rowSelection && (
                                <th className="data-table__th data-table__th--checkbox">
                                    <input
                                        type="checkbox"
                                        checked={table.getIsAllRowsSelected()}
                                        onChange={table.getToggleAllRowsSelectedHandler()}
                                    />
                                </th>
                            )}
                            {headerGroup.headers.map((header) => (
                                <th
                                    key={header.id}
                                    className={`data-table__th${
                                        header.column.getCanSort()
                                            ? ' data-table__th--sortable'
                                            : ''
                                    }`}
                                    onClick={header.column.getToggleSortingHandler()}
                                    style={{
                                        width: getColumnWidth(
                                            header.column.columnDef
                                        ),
                                        maxWidth: getColumnWidth(
                                            header.column.columnDef
                                        ),
                                    }}
                                >
                                    <div className="data-table__th-content">
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                  header.column.columnDef
                                                      .header,
                                                  header.getContext()
                                              )}
                                        {sorting &&
                                            header.column.getCanSort() && (
                                                <span className="data-table__sort-icon">
                                                    {header.column.getIsSorted() ===
                                                    'asc'
                                                        ? '▲'
                                                        : header.column.getIsSorted() ===
                                                            'desc'
                                                          ? '▼'
                                                          : '⇅'}
                                                </span>
                                            )}
                                        {columnFiltering &&
                                            header.column.getCanFilter() && (
                                                <button
                                                    className={`data-table__filter-toggle${
                                                        filtersVisible[
                                                            header.id
                                                        ]
                                                            ? ' data-table__filter-toggle--active'
                                                            : ''
                                                    }`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setFiltersVisible(
                                                            (prev) => ({
                                                                ...prev,
                                                                [header.id]:
                                                                    !prev[
                                                                        header
                                                                            .id
                                                                    ],
                                                            })
                                                        );
                                                    }}
                                                    title="Toggle filter"
                                                >
                                                    <span className="Appkit4-icon icon-filter-outline" />
                                                </button>
                                            )}
                                    </div>
                                    {columnFiltering &&
                                        filtersVisible[header.id] &&
                                        header.column.getCanFilter() && (
                                            <input
                                                className="data-table__column-filter"
                                                value={
                                                    (header.column.getFilterValue() as string) ??
                                                    ''
                                                }
                                                onChange={(e) =>
                                                    header.column.setFilterValue(
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Filter..."
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                                autoFocus
                                            />
                                        )}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map((row) => (
                        <React.Fragment key={row.id}>
                            <tr
                                className={`data-table__tr${
                                    onRowClick
                                        ? ' data-table__tr--clickable'
                                        : ''
                                }${
                                    rowSelection && row.getIsSelected()
                                        ? ' data-table__tr--selected'
                                        : ''
                                }`}
                                onClick={() => onRowClick?.(row.original)}
                            >
                                {rowSelection && (
                                    <td className="data-table__td data-table__td--checkbox">
                                        <input
                                            type="checkbox"
                                            checked={row.getIsSelected()}
                                            onChange={row.getToggleSelectedHandler()}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </td>
                                )}
                                {row.getVisibleCells().map((cell) => {
                                    const columnWidth = getColumnWidth(
                                        cell.column.columnDef
                                    );
                                    return (
                                        <td
                                            key={cell.id}
                                            className="data-table__td"
                                            style={{
                                                width: columnWidth,
                                                maxWidth: columnWidth,
                                            }}
                                        >
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                            {expandable &&
                                row.getIsExpanded() &&
                                renderExpandedRow && (
                                    <tr className="data-table__tr data-table__tr--expanded">
                                        <td
                                            colSpan={
                                                row.getVisibleCells().length +
                                                (rowSelection ? 1 : 0)
                                            }
                                            className="data-table__td data-table__td--expanded"
                                        >
                                            {renderExpandedRow(row)}
                                        </td>
                                    </tr>
                                )}
                        </React.Fragment>
                    ))}
                    {table.getRowModel().rows.length === 0 && (
                        <tr>
                            <td
                                colSpan={
                                    columns.length + (rowSelection ? 1 : 0)
                                }
                                className="data-table__td data-table__td--empty"
                            >
                                No data available
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

export default DataTable;
